import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@mui/material';
import { di } from '../../../state/inversify.config';
import { ViewIncidentDomainService } from '../../../pages/viewIncident/viewIncidentDomainService';
import { UserNotificationContext } from '../../../components/userNotification/userNotificationContext';
import { UserNotificationType } from '../../../components/userNotification/userNotificationType.enum';
import { AssessmentRepository } from '../../../state/reduxRepositories';
import { AuthService } from '../../../state/helperServices/auth/auth.service';
import styles from './gpClearance.module.scss';
import { getRouteByAccountType } from '../../../routes/getRouteByAccountType';
import assessmentSpecifications from './gp_clearance_spec.json';
import { version } from '../../../version';
import { AlertDialog } from '../../../components/alertDialog/alertDialog';
import { Button } from '../../../components/button/button';

export function GPClearance() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addUserNotification } = useContext(UserNotificationContext);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  const viewIncidentDomainService = di.get<ViewIncidentDomainService>(
    ViewIncidentDomainService,
  );
  const selectedIncident =
    viewIncidentDomainService.useSelectedIncidentSelector();
  const selectedPlayer = viewIncidentDomainService.useSelectedPlayerSelector();

  const assessmentRepository =
    di.get<AssessmentRepository>(AssessmentRepository);

  const authService = di.get<AuthService>(AuthService);
  const loggedInUser = authService.useLoggedInUserSelector();

  const gpClearance = () => {
    if (!selectedIncident) {
      addUserNotification(
        UserNotificationType.ERROR,
        t('incident.INCIDENT_SELECTION_REQUIRED'),
        UserNotificationType.ERROR,
      );
      return;
    }

    setShowConfirmationModal(true);
  };

  const confirmedClearance = () => {
    setShowConfirmationModal(false);

    assessmentRepository.dispatchCreate({
      assessment: {
        name: 'assessment',
        assessmentType: assessmentSpecifications.info.identifier,
        assessmentVersion: assessmentSpecifications.info.version,
        createdOnSoftwareVersion: version,
        info: JSON.stringify(assessmentSpecifications),
        assessment: { gpClearance: true },
        clearedToPlay: true,
        pass: true,
      },
      playerId: selectedPlayer.id,
      playerFullName: `${selectedPlayer.firstName} ${selectedPlayer.lastName}`,
      timeZoneOffset: new Date().getTimezoneOffset(),
    });

    setShowSummary(true);
  };

  return (
    <>
      <Button
        type="submit"
        color="secondary"
        variant="contained"
        onClick={gpClearance}
        sx={{ my: 2 }}
        label={t('GUARDIAN_GP_CLEARANCE')}
      />

      <AlertDialog
        actions
        title={t('GUARDIAN_GP_CLEARANCE')}
        open={showConfirmationModal}
        onConfirm={confirmedClearance}
        onDismiss={() => setShowConfirmationModal(false)}
        setOpen={setShowConfirmationModal}
        content={t('GUARDIAN_GP_CLEARANCE_DETAILS')}
        confirmLabel={t('CONFIRM')}
        cancelLabel={t('CANCEL')}
      />
      {showSummary && (
        <div className={styles.summaryContainer}>
          <Grid>
            <Grid item textAlign="center" sx={{ mt: 3 }}>
              <Typography variant="h6">
                {t('GP_CLEARANCE_SUMMARY_TITLE', {
                  user: `${loggedInUser.firstName} ${loggedInUser.lastName}`,
                  player: `${selectedPlayer.firstName} ${selectedPlayer.lastName}`,
                })}
              </Typography>
            </Grid>
            <Grid item textAlign="center" sx={{ mt: 3 }}>
              <CheckCircleIcon color="success" />
            </Grid>
            <Grid item textAlign="center" sx={{ mt: 3 }}>
              <Typography variant="subtitle1">
                {t('GP_CLEARANCE_SUMMARY_SUBTITLE', {
                  player: `${selectedPlayer.firstName} ${selectedPlayer.lastName}`,
                })}
              </Typography>
            </Grid>
            <Grid item textAlign="center" sx={{ mt: 3 }}>
              <Button
                type="submit"
                variant="contained"
                onClick={() =>
                  navigate(
                    `/${getRouteByAccountType(loggedInUser.accountType)}`,
                  )
                }
                color="secondary"
                label={t('HOME')}
              />
            </Grid>
          </Grid>
        </div>
      )}
    </>
  );
}
